export const resourceCardsFromToolCalls = (toolCalls) => {
  const cards = toolCalls.flatMap((toolCall) => toolCall.resource_cards || []);

  return cards.filter((card, index) => (
    cards.findIndex((existingCard) => (
      existingCard.id === card.id &&
      existingCard.record_type === card.record_type
    )) === index
  ));
};

export const classifiedResourceCards = (toolCalls) => {
  const created = resourceCardsFromToolCalls(toolCalls.filter((toolCall) => toolCall.creates_resources));
  const createdKeys = new Set(created.map((card) => `${card.record_type}-${card.id}`));
  const referenced = resourceCardsFromToolCalls(toolCalls.filter((toolCall) => !toolCall.creates_resources))
    .filter((card) => !createdKeys.has(`${card.record_type}-${card.id}`));

  return { created, referenced };
};

const messageTime = (message) => Date.parse(message.created_at);

// A streaming turn saves its assistant message empty before the model runs, so a
// tool call is usually NEWER than the reply it belongs to. Bind by turn instead:
// the reply that follows the most recent user message before the tool call, and
// only fall back to the next reply when no user message precedes it.
const assistantMessageForToolCall = (assistantMessages, userMessages, toolCallTime) => {
  const turnStart = userMessages.filter((message) => messageTime(message) <= toolCallTime).pop();

  if (turnStart) {
    const replyInTurn = assistantMessages.find((message) => messageTime(message) >= messageTime(turnStart));
    if (replyInTurn) return replyInTurn;
  }

  return assistantMessages.find((message) => messageTime(message) >= toolCallTime) || null;
};

// Server messages don't carry per-message tool calls, so reloaded threads have to
// re-attribute each tool call to its turn. Live-turn tool calls are bound
// explicitly by onComplete and skip this path.
const toolCallsByMessage = (messages, toolCalls) => {
  const boundToolCalls = new Set(messages.flatMap((message) => message.tool_calls || []));
  const datedMessages = messages.filter((message) => !Number.isNaN(messageTime(message)));
  const assistantMessages = datedMessages.filter((message) => message.role === 'assistant');
  const userMessages = datedMessages.filter((message) => message.role === 'user');
  const toolCallsByMessageId = {};

  const assign = (messageId, toolCall) => {
    if (!toolCallsByMessageId[messageId]) toolCallsByMessageId[messageId] = [];
    toolCallsByMessageId[messageId].push(toolCall);
  };

  messages.forEach((message) => {
    (message.tool_calls || []).forEach((toolCall) => assign(message.id, toolCall));
  });

  toolCalls.forEach((toolCall) => {
    if (boundToolCalls.has(toolCall)) return;

    const toolCallTime = Date.parse(toolCall.timestamp || toolCall.created_at || '');
    if (Number.isNaN(toolCallTime)) return;

    const target = assistantMessageForToolCall(assistantMessages, userMessages, toolCallTime);
    if (!target) return;

    assign(target.id, toolCall);
  });

  return toolCallsByMessageId;
};

export const messagesWithToolCalls = (messages, toolCalls) => {
  const toolCallsByMessageId = toolCallsByMessage(messages, toolCalls);

  return messages.map((message) => (toolCallsByMessageId[message.id]
    ? { ...message, tool_calls: toolCallsByMessageId[message.id] }
    : message));
};

export const createdCardsByMessage = (messages, toolCalls) => {
  const toolCallsByMessageId = toolCallsByMessage(messages, toolCalls);

  return Object.fromEntries(
    Object.entries(toolCallsByMessageId)
      .map(([messageId, calls]) => {
        const { created, referenced } = classifiedResourceCards(calls);
        return [messageId, [...created, ...referenced.filter((card) => card.inline)]];
      })
      .filter(([, cards]) => cards.length > 0)
  );
};

export const threadToolCallsFrom = (messages, completedToolCalls) => {
  const toolCalls = [
    ...messages.flatMap((message) => message.tool_calls || []),
    ...completedToolCalls
  ];
  const seenKeys = new Set();

  return toolCalls.filter((toolCall) => {
    const key = toolCall.id
      ? `id-${toolCall.id}`
      : `${toolCall.tool_name}-${toolCall.timestamp || toolCall.created_at || ''}-${toolCall.summary || ''}`;

    if (seenKeys.has(key)) return false;

    seenKeys.add(key);
    return true;
  });
};

export const advanceThreadUsage = (currentUsage, llmMetadata) => {
  const usage = llmMetadata?.usage;
  if (!usage) return currentUsage;

  const contextTokens = (usage.input_tokens || 0) +
    (usage.cached_tokens || 0) +
    (usage.cache_creation_tokens || 0);

  return {
    ...(currentUsage || {}),
    request_count: (currentUsage?.request_count || 0) + 1,
    total_tokens: (currentUsage?.total_tokens || 0) + (usage.total_tokens || 0),
    last_context_tokens: contextTokens,
    last_context_window_tokens: llmMetadata.context_window_tokens || currentUsage?.last_context_window_tokens,
    last_model_id: llmMetadata.model_id || currentUsage?.last_model_id
  };
};

export const contextPercentage = (usage) => {
  const contextTokens = usage?.last_context_tokens;
  const contextWindowTokens = usage?.last_context_window_tokens;
  if (!contextTokens || !contextWindowTokens) return null;

  return Math.min(100, Math.max(0, (contextTokens / contextWindowTokens) * 100));
};
